import React, { useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { currencyCodes } from '~/globals';
import dayjs from '~/timeInit';

import { Autocomplete, Box, Chip, Container, Stack, TextField, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import MarkdownPreview from '~/components/Common/MarkdownPreview';
import Spinner from '~/components/Common/Spinner';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import VoucherConditions from '~/components/Vouchers/VoucherConditions';
import VoucherLogsTable from '~/components/Vouchers/VoucherLogsTable';
import VoucherRewards from '~/components/Vouchers/VoucherRewards';
import VoucherUsers from '~/components/Vouchers/VoucherUsers';

import VoucherFormNavigation from '~/pages/Vouchers/VoucherFormNavigation';

import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';

import {
  CreateVoucherPayload,
  CreateVoucherResponse,
  UpdateVoucherPayload,
  UpdateVoucherResponse,
  Voucher,
  createVoucher,
  getVoucher,
  updateVoucher,
} from '~/services/PromoService';

type RouteParams = {
  voucherId?: string;
};

type VoucherReward = NonNullable<CreateVoucherPayload['voucher_rewards']>[number];
type VoucherCondition = NonNullable<CreateVoucherPayload['voucher_conditions']>[number];
type VoucherToggleValue = string;
type VoucherToggleOption = {
  key: string;
  isActive: boolean;
  internalDescription: string;
  internalDescriptionLong: string;
};
type VoucherApiFields = {
  voucherName?: string;
  voucherTitle?: string;
  voucherDescription?: string;
  currencyCode?: string;
  voucherRewards?: VoucherReward[];
  voucherConditions?: VoucherCondition[];
  voucherToggles?: VoucherToggleValue[];
  voucher_name?: string;
  voucher_title?: string;
  voucher_description?: string;
  currency_code?: string;
  voucher_rewards?: VoucherReward[];
  voucher_conditions?: VoucherCondition[];
  voucher_toggles?: VoucherToggleValue[];
};

const DEFAULT_REWARD_AMOUNT = 50;
const DEFAULT_REWARD_PRODUCTS: Array<VoucherReward['products'][number]> = ['hotel-ALL', 'tour-ALL', 'cruise-ALL'];

type VoucherFormSnapshotInput = {
  voucherName: string;
  voucherTitle: string;
  voucherDescription: string;
  currencyCode: string;
  voucherRewards: VoucherReward[];
  voucherConditions: VoucherCondition[];
  voucherToggles: VoucherToggleValue[];
};

const buildFormSnapshot = (data: VoucherFormSnapshotInput) => {
  return JSON.stringify({
    voucherName: data.voucherName.trim(),
    voucherTitle: data.voucherTitle,
    voucherDescription: data.voucherDescription,
    currencyCode: data.currencyCode,
    voucherRewards: data.voucherRewards.map((reward) => {
      const minSpendProducts = (reward.min_spend_products ?? []).filter(Boolean);
      return {
        ...reward,
        reward_amount: Number(reward.reward_amount),
        min_spend: reward.min_spend == null ? undefined : Number(reward.min_spend),
        ...(minSpendProducts.length > 0 ? { min_spend_products: minSpendProducts } : {}),
      };
    }),
    voucherConditions: data.voucherConditions,
    voucherToggles: data.voucherToggles,
  });
};

const normalizeExpiredAt = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }
  return trimmedValue.includes('T') ? trimmedValue : `${trimmedValue}T00:00:00.000Z`;
};

const buildDefaultRewards = (): VoucherReward[] => {
  return [
    {
      id_voucher_reward: uuid(),
      reward_amount: DEFAULT_REWARD_AMOUNT,
      products: DEFAULT_REWARD_PRODUCTS,
      min_spend: undefined,
      min_spend_products: [],
    },
  ];
};

export default function VoucherFormPage() {
  const { voucherId } = useParams<RouteParams>();
  const [showAdvanceDescription, setShowAdvanceDescription] = useState(false);
  const isEdit = Boolean(voucherId);
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voucherName, setVoucherName] = useState('');
  const [voucherTitle, setVoucherTitle] = useState('');
  const [voucherDescription, setVoucherDescription] = useState('');
  const [expiredAt, setExpiredAt] = useState('');
  const [currencyCode, setCurrencyCode] = useState('AUD');
  const [voucherRewards, setVoucherRewards] = useState<VoucherReward[]>([]);
  const [voucherConditions, setVoucherConditions] = useState<VoucherCondition[]>([]);
  const [voucherToggles, setVoucherToggles] = useState<VoucherToggleValue[]>([]);
  const [voucherUsers, setVoucherUsers] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ voucherName?: string; currencyCode?: string }>({});
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  const { data: voucherResponse, isLoading: isVoucherLoading } = useTanstackQuery({
    queryKey: ['voucher', voucherId],
    queryFn: async () => {
      if (!voucherId) {
        return null;
      }
      return await getVoucher(voucherId);
    },
    enabled: Boolean(voucherId),
  });

  const voucher: Voucher | null = useMemo(() => {
    return voucherResponse?.result ?? null;
  }, [voucherResponse]);

  useEffect(() => {
    if (!isEdit) {
      const defaultRewards = buildDefaultRewards();
      setVoucherName('');
      setVoucherTitle('');
      setVoucherDescription('');
      setExpiredAt('');
      setCurrencyCode('AUD');
      setVoucherRewards(defaultRewards);
      setVoucherConditions([]);
      setVoucherToggles([]);
      setVoucherUsers([]);
      setErrors({});
      setInitialSnapshot(
        buildFormSnapshot({
          voucherName: '',
          voucherTitle: '',
          voucherDescription: '',
          currencyCode: 'AUD',
          voucherRewards: defaultRewards,
          voucherConditions: [],
          voucherToggles: [],
        }),
      );
      return;
    }
    if (voucher) {
      const voucherData = voucher as VoucherApiFields;
      const nextVoucherName = voucherData.voucherName || voucherData.voucher_name || '';
      const nextVoucherTitle = voucherData.voucherTitle || voucherData.voucher_title || '';
      const nextVoucherDescription = voucherData.voucherDescription || voucherData.voucher_description || '';
      if (
        (voucherData.currencyCode || voucherData.currency_code) &&
        currencyCodes.includes((voucherData.currencyCode || voucherData.currency_code) as string)
      ) {
        setCurrencyCode((voucherData.currencyCode || voucherData.currency_code) as string);
      } else {
        setCurrencyCode('AUD');
      }
      const nextVoucherRewards = (voucherData.voucherRewards ?? voucherData.voucher_rewards ?? []).map((reward) => ({
        ...reward,
        id_voucher_reward: reward.id_voucher_reward || uuid(),
      }));
      const nextVoucherConditions = (voucherData.voucherConditions ?? voucherData.voucher_conditions ?? []).map(
        (condition) => ({
          ...condition,
          id_voucher_condition: condition.id_voucher_condition || uuid(),
        }),
      );
      const nextVoucherToggles = voucherData.voucherToggles ?? voucherData.voucher_toggles ?? [];
      setVoucherName(nextVoucherName);
      setVoucherTitle(nextVoucherTitle);
      setVoucherDescription(nextVoucherDescription);
      setVoucherRewards(nextVoucherRewards);
      setVoucherConditions(nextVoucherConditions);
      setVoucherToggles(nextVoucherToggles);
      setVoucherUsers([]);
      setInitialSnapshot(
        buildFormSnapshot({
          voucherName: nextVoucherName,
          voucherTitle: nextVoucherTitle,
          voucherDescription: nextVoucherDescription,
          currencyCode:
            (voucherData.currencyCode || voucherData.currency_code) &&
            currencyCodes.includes((voucherData.currencyCode || voucherData.currency_code) as string)
              ? ((voucherData.currencyCode || voucherData.currency_code) as string)
              : 'AUD',
          voucherRewards: nextVoucherRewards,
          voucherConditions: nextVoucherConditions,
          voucherToggles: nextVoucherToggles,
        }),
      );
    }
  }, [isEdit, voucher]);

  const currentSnapshot = useMemo(() => {
    return buildFormSnapshot({
      voucherName,
      voucherTitle,
      voucherDescription,
      currencyCode,
      voucherRewards,
      voucherConditions,
      voucherToggles,
    });
  }, [voucherName, voucherTitle, voucherDescription, currencyCode, voucherRewards, voucherConditions, voucherToggles]);

  const hasUnsavedChanges = initialSnapshot !== null && initialSnapshot !== currentSnapshot;
  const voucherToggleOptions: VoucherToggleOption[] = promoMeta?.voucher_toggles ?? [];

  const selectedUnknownVoucherToggleOptions = useMemo<VoucherToggleOption[]>(() => {
    const knownToggleKeys = new Set(voucherToggleOptions.map((option) => option.key));
    return voucherToggles
      .filter((key) => !knownToggleKeys.has(key))
      .map((key) => ({
        key,
        isActive: false,
        internalDescription: `[UNKNOWN] ${key}`,
        internalDescriptionLong:
          'This toggle key is not in current voucher metadata and will be preserved unless explicitly removed.',
      }));
  }, [voucherToggles, voucherToggleOptions]);

  const voucherToggleOptionsWithUnknown = useMemo<VoucherToggleOption[]>(() => {
    return [...voucherToggleOptions, ...selectedUnknownVoucherToggleOptions];
  }, [voucherToggleOptions, selectedUnknownVoucherToggleOptions]);

  const selectedVoucherToggleOptions = useMemo<VoucherToggleOption[]>(() => {
    const optionByKey = new Map(voucherToggleOptionsWithUnknown.map((option) => [option.key, option] as const));
    return voucherToggles
      .map((key) => optionByKey.get(key))
      .filter((option): option is VoucherToggleOption => Boolean(option));
  }, [voucherToggles, voucherToggleOptionsWithUnknown]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }
    const unblock = history.block(() => {
      const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
      if (!shouldLeave) {
        return false;
      }
      return undefined;
    });
    return () => unblock();
  }, [hasUnsavedChanges, history]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'users') {
      const usersElement = document.getElementById('users');
      if (usersElement) {
        usersElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [location.hash]);

  const createMutation = useMutation<CreateVoucherResponse, Error, CreateVoucherPayload>({
    mutationFn: async (payload) => createVoucher(payload),
  });

  const updateMutation = useMutation<UpdateVoucherResponse, Error, { id: string; payload: UpdateVoucherPayload }>({
    mutationFn: async ({ id, payload }) => updateVoucher(id, payload),
  });

  const createVoucherUsersMutation = useCreateVoucherUsers();

  const validateForm = () => {
    const newErrors: { voucherName?: string; currencyCode?: string } = {};
    if (!voucherName.trim()) {
      newErrors.voucherName = 'Voucher name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedRewards = voucherRewards.map((reward) => {
        const minSpendProducts = (reward.min_spend_products ?? []).filter(Boolean);
        return {
          ...reward,
          reward_amount: Number(reward.reward_amount),
          min_spend: reward.min_spend == null ? undefined : Number(reward.min_spend),
          ...(minSpendProducts.length > 0 ? { min_spend_products: minSpendProducts } : {}),
        };
      });
      let savedVoucherId = voucherId || '';
      if (isEdit) {
        const payload: UpdateVoucherPayload & {
          voucher_toggles?: CreateVoucherPayload['voucher_toggles'];
        } = {
          ...(voucherName.trim() ? { voucher_name: voucherName.trim() } : {}),
          voucher_title: voucherTitle,
          voucher_description: voucherDescription,
          ...(currencyCode ? { currency_code: currencyCode } : { currency_code: 'AUD' }),
          voucher_rewards: normalizedRewards as UpdateVoucherPayload['voucher_rewards'],
          voucher_conditions: voucherConditions,
          voucher_toggles: voucherToggles as CreateVoucherPayload['voucher_toggles'],
        };
        await updateMutation.mutateAsync({ id: voucherId || '', payload });
      } else {
        const payload: CreateVoucherPayload = {
          voucher_name: voucherName.trim(),
          voucher_title: voucherTitle,
          voucher_description: voucherDescription,
          currency_code: 'AUD',
          voucher_rewards: normalizedRewards,
          voucher_conditions: voucherConditions,
          voucher_toggles: voucherToggles as CreateVoucherPayload['voucher_toggles'],
        };
        const createResponse = await createMutation.mutateAsync(payload);
        savedVoucherId = createResponse?.result?.voucherId || '';
      }

      if (voucherUsers.length > 0 && savedVoucherId) {
        const resolvedExpiredAt = expiredAt.trim()
          ? normalizeExpiredAt(expiredAt)
          : normalizeExpiredAt(dayjs().add(1, 'year').format('YYYY-MM-DD'));
        await createVoucherUsersMutation.mutateAsync({
          voucher_id: savedVoucherId,
          expired_at: resolvedExpiredAt,
          voucher_users: voucherUsers.map((userId) => ({ user_id: userId })),
        });
      }
      setInitialSnapshot(currentSnapshot);
      enqueueSnackbar(isEdit ? 'Updated voucher successfully' : 'Created voucher successfully', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      if (!isEdit && savedVoucherId) {
        history.replace(`/marketing/vouchers/${savedVoucherId}`);
      }
    } catch (error) {
      enqueueSnackbar(`Failed to save voucher: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEdit && isVoucherLoading) {
    return <Spinner />;
  }

  return (
    <Container maxWidth="xl">
      <PageHeader title={isEdit ? 'Edit Voucher' : 'Create Voucher'}>
        <VoucherFormNavigation
          isSubmitting={isSubmitting}
          isEdit={isEdit}
          showAdvanceDescription={showAdvanceDescription}
          onBack={() => history.push('/marketing/vouchers')}
          onSubmit={handleSubmit}
          onToggleAdvancedDescription={() => setShowAdvanceDescription(!showAdvanceDescription)}
        />
      </PageHeader>

      <Stack spacing={10}>
        <Stack spacing={2}>
          <TextField
            label="Voucher Name"
            value={voucherName}
            onChange={(e) => {
              setVoucherName(e.target.value);
              if (errors.voucherName && e.target.value.trim()) {
                setErrors({ ...errors, voucherName: undefined });
              }
            }}
            error={!!errors.voucherName}
            helperText={
              errors.voucherName || showAdvanceDescription
                ? 'Name displayed for the voucher. This will be displayed to the user during checkout.'
                : undefined
            }
            fullWidth
            required
            disabled={isSubmitting}
          />
          <TextField
            label="Voucher Title"
            value={voucherTitle}
            onChange={(event) => {
              setVoucherTitle(event.target.value);
            }}
            helperText={
              showAdvanceDescription
                ? 'Optional title displayed for this voucher to the user when available.'
                : undefined
            }
            fullWidth
            disabled={isSubmitting}
          />
          {voucherTitle.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Voucher Title Preview
              </Typography>
              <MarkdownPreview content={voucherTitle} boxProps={{ mt: 1 }} />
            </Box>
          )}
          <TextField
            label="Voucher Description"
            value={voucherDescription}
            onChange={(event) => {
              setVoucherDescription(event.target.value);
            }}
            helperText={
              showAdvanceDescription ? 'Optional description shown to the user to explain voucher details.' : undefined
            }
            fullWidth
            multiline
            minRows={2}
            disabled={isSubmitting}
          />
          {voucherDescription.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Voucher Description Preview
              </Typography>
              <MarkdownPreview content={voucherDescription} boxProps={{ mt: 1 }} />
            </Box>
          )}
          {/* <TextField
            label="Currency Code"
            value={currencyCode}
            onChange={(event) => {
              setCurrencyCode(event.target.value as string);
              if (errors.currencyCode) {
                setErrors({ ...errors, currencyCode: undefined });
              }
            }}
            error={!!errors.currencyCode}
            helperText={errors.currencyCode || showAdvanceDescription ? 'Select an ISO currency code' : undefined}
            fullWidth
            required
            disabled={isSubmitting}
            select
          >
            {currencyCodes.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>*/}
          {!isPromoMetaLoading && promoMeta && promoMeta.voucher_toggles.length > 0 && (
            <Autocomplete
              multiple
              options={voucherToggleOptionsWithUnknown}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              getOptionLabel={(option) => `${!option.isActive ? '[INACTIVE] - ' : ''}${option.internalDescription}`}
              value={selectedVoucherToggleOptions}
              onChange={(_event, newValue) => {
                setVoucherToggles(newValue.map((option) => option.key));
              }}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Voucher Toggles" />}
              renderOption={(props, option) => (
                <li
                  {...props}
                  title={
                    option.isActive ? option.internalDescriptionLong : `Inactive - ${option.internalDescriptionLong}`
                  }
                >
                  {`${!option.isActive ? '[INACTIVE] - ' : ''}${option.internalDescription}`}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.key}
                    {...getTagProps({ index })}
                    label={`${!option.isActive ? '[INACTIVE] - ' : ''}${option.internalDescription}`}
                    title={
                      option.isActive
                        ? option.internalDescriptionLong
                        : `Inactive - this toggle will not be applied - ${option.internalDescriptionLong}`
                    }
                  />
                ))
              }
              disabled={isSubmitting}
            />
          )}
        </Stack>
        <Box mt={3}>
          <PageSubheader title="Rewards">
            {showAdvanceDescription && (
              <Box component="span" color="text.secondary" alignItems="left">
                The amount the voucher is worth, and for which products. Only one reward is applied to an order.
              </Box>
            )}
          </PageSubheader>
          <VoucherRewards rewards={voucherRewards} onChange={setVoucherRewards} disabled={isSubmitting} />
        </Box>
        <Box mt={3}>
          <PageSubheader title="Conditions" />
          <VoucherConditions
            conditions={voucherConditions}
            rewards={voucherRewards}
            onChange={setVoucherConditions}
            showAdvanceDescription={showAdvanceDescription}
            disabled={isSubmitting}
          />
        </Box>
        <VoucherUsers
          userIds={voucherUsers}
          onChange={setVoucherUsers}
          expiredAt={expiredAt}
          onExpiredAtChange={setExpiredAt}
          showAdvanceDescription={showAdvanceDescription}
          disabled={isSubmitting}
          voucherId={voucherId}
        />
        {isEdit && (
          <VoucherLogsTable
            voucherId={voucherId}
            queryOptions={{
              page: 0,
              limit: 20,
              sortBy: 'created_at',
              sortByDirection: 'desc',
            }}
          />
        )}
      </Stack>
    </Container>
  );
}
